import { useCallback } from 'react';

import { View } from 'framework-listing-lib/types';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useApplicationId from 'framework-listing-lib/internal/hooks/useApplicationId';
import {
  useCurrentViewId,
  useViewsNamespace,
} from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';

import { convertViewToCustomerDataView } from '../utils/mapper';
import { useUpdateSingleDefaultViewMutation } from 'customer-data-views-management/singleView/hooks/useUpdateSingleDefaultViewMutation';
import { useFetchSingleView } from 'customer-data-views-management/singleView/hooks/useFetchSingleView';

// This should be correct enough to prevent render-time errors
// while the currentView loads
// but should also not overwrite any actual existing view
// if somehow the mutation is called during the loading state
export const PLACEHOLDER_VIEW = {
  id: '',
  name: '',
  columns: [],
  state: {} as any,
  filterGroups: [],
  quickFilters: [],
  quickFilterProperties: [],
  namespaceName: '',
  objectTypeId: '',
  ownerId: undefined,
  private: false,
  teamId: undefined,
  type: 'STANDARD' as const,
};

/**
 * Saving a default view is a silent, auto-save style operation: it reports
 * success/failure through metrics only, never through a localized alert.
 *
 * Keep this module free of I18n imports (directly or transitively). It is
 * reachable from `ViewsProvider`, which every consumer of the library mounts,
 * so pulling translations in here forces every downstream app to bundle
 * `framework-listing-lib/lang/*.lyaml` (enforced by the
 * `validate-dependency-lyaml-present` build validation).
 */
export function useSaveDefaultView() {
  'use memo';
  const objectType = useObjectTypeId();
  const namespace = useViewsNamespace();
  const currentViewId = useCurrentViewId();
  const applicationId = useApplicationId();

  // We use the customer-data-views-management hook directly
  // instead of our context value
  // because we want to use the CDVM View interface
  const { data: currentViewData } = useFetchSingleView({
    id: currentViewId || '',
    objectTypeId: objectType,
    namespaceName: namespace,
  });
  const [updateDefaultViewMutation, { loading }] =
    useUpdateSingleDefaultViewMutation({
      view: currentViewData?.singleViewById || PLACEHOLDER_VIEW,
    });

  const saveDefaultView = useCallback(
    (view: View) => {
      return updateDefaultViewMutation({
        variables: convertViewToCustomerDataView({ view }),
      })
        .then(() => {
          Metrics.counter('update-default-view-success', {
            application_id: applicationId,
          }).increment();
        })
        .catch(() => {
          Metrics.counter('update-default-view-failure', {
            application_id: applicationId,
          }).increment();
        });
    },
    [applicationId, updateDefaultViewMutation]
  );

  return {
    loading,
    saveDefaultView,
  };
}
